import { Grid } from '@material-ui/core';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { createSelectField, createTextField } from '@roc/client-portal-shared/components';
import { AutocompleteAddressField, GoogleMap } from '@roc/ui';
import { observer } from 'mobx-react';
import { isNil, isNotBlank, statesAbbreviation, statesNames } from '@roc/feature-utils';

const useAddressStyles = makeStyles((theme: Theme) => ({
  mapStyles: {
    position: 'relative',
    width: '100%',
    height: '150px',
  },
  disabledClass: {
    background: '#eee'
  },
  disabledAutoCompleteClass: {
    '& .MuiInputBase-root': {
      background: '#eee'
    }
  }
}));

const stateOptions = statesAbbreviation.map((abbreviation, i) => ({
  value: abbreviation,
  label: statesNames[i],
}));

const addressLine2Tooltip =
  'Address Line 2 can be used for additional information\nsuch as apartment number, suite number, unit number\nor others details that can help in identifying the\nexact location of the address.';

const STREET_NUMBER_REGEX = /^(\d+)\s(.*)$/;

interface PropertyAddressDetailsProps {
  testId?: string;
  store: any;
  disabled?: boolean;
  standaloneLabel?: boolean;
  showMap?: boolean;
  autoFocus?: boolean;
  addMarkForRequiredFields?: boolean;
}

const PropertyAddressDetails = observer(
  ({
    testId = 'address',
    store,
    disabled = false,
    standaloneLabel = true,
    showMap = true,
    autoFocus = false,
    addMarkForRequiredFields = true,
  }: PropertyAddressDetailsProps) => {
    const classes = useAddressStyles();

    const formFields = store.form.fields;

    const handleAddressChange = event => {
      const { value, fullInfo } = event?.target || {};
      const { address_components, geometry } = fullInfo || {};
      if (!isNil(address_components) && !isNil(geometry)) {
        store.saveAddressField(value, address_components, geometry);
      } else {
        const address = event.target.value;
        store.onFieldChange('address', address);
        if (STREET_NUMBER_REGEX.test(address)) {
          const [, streetNumber, streetName] = address.match(
            STREET_NUMBER_REGEX
          );
          store.onFieldChange('streetName', streetName);
          store.onFieldChange('streetNumber', streetNumber);
        } else {
          store.onFieldChange('streetName', address);
          store.onFieldChange('streetNumber', '');
        }
      }
    };

    const stateOptionsFiltered = [...stateOptions];
    stateOptionsFiltered.shift();

    const showGoogleMap =
      showMap &&
      Boolean(formFields?.latitude?.value && formFields?.longitude?.value);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <AutocompleteAddressField
            label="Address Line 1"
            disabled={disabled}
            readOnly={disabled}
            required={true}
            name="address"
            value={formFields.address.value}
            error={
              isNotBlank(formFields.address.error) ||
              isNotBlank(formFields.__isAddressInCorrectFormat__.error)
            }
            errorText={
              formFields.address.error ||
              formFields.__isAddressInCorrectFormat__.error
            }
            onChange={handleAddressChange}
            fullWidth
            testId={`address-${testId}`}
            standaloneLabel={standaloneLabel}
            autoFocus={autoFocus}
            inputClassname={disabled ? classes.disabledAutoCompleteClass : ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {createTextField({
            label: 'Address Line 2',
            fieldName: 'aptNumber',
            testId: `aptNumber-${testId}`,
            store: store,
            disabled,
            tooltip: addressLine2Tooltip,
            standaloneLabel,
            InputProps: {
              style: {
                background: disabled ? '#eee' : 'none'
              }
            }

          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createTextField({
            label: 'City',
            fieldName: 'city',
            testId: `city-${testId}`,
            store: store,
            disabled,
            standaloneLabel,
            InputProps: {
              style: {
                background: disabled ? '#eee' : 'none'
              }
            }
          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createSelectField({
            store: store,
            testId: `state-${testId}`,
            fieldName: 'state',
            label: 'State',
            options: stateOptionsFiltered,
            disabled,
            standaloneLabel,
            className: disabled ? classes.disabledClass : ''
          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createTextField({
            label: 'Zip Code',
            fieldName: 'zipCode',
            testId: `zipCode-${testId}`,
            store: store,
            disabled,
            standaloneLabel,
            InputProps: {
              style: {
                background: disabled ? '#eee' : 'none'
              }
            }
          })}
        </Grid>
        {showGoogleMap && (
          <Grid item xs={12}>
            <GoogleMap
              mapStyles={classes.mapStyles}
              latitude={formFields.latitude.value}
              longitude={formFields.longitude.value}
            />
          </Grid>
        )}
      </Grid>
    );
  }
);

export const PrimaryAddress = observer(
  ({ store, disabled = false }: { store: any, disabled?: boolean }) => {
    return (
      <>
        <PropertyAddressDetails
          testId={`${store.key}-addressComponent`}
          store={store}
          showMap={false}
          standaloneLabel={false}
          addMarkForRequiredFields={true}
          disabled={disabled}
        />
      </>
    );
  }
);